import { useEffect } from 'react';
import { ThemeTokens } from '@repo/types';

// Map theme token keys to CSS variable names
const tokenToCssVar: Record<keyof ThemeTokens, string> = {
  // Colors - Backgrounds
  colorBackgroundPrimaryLight: '--color-background-primary-light',
  colorBackgroundSecondaryLight: '--color-background-secondary-light',
  colorBackgroundAlternative: '--color-background-alternative',
  colorBackgroundHeader: '--color-background-header',
  // Colors - Text
  colorTextPrimaryOnLight: '--color-text-primary-on-light',
  colorTextSecondaryOnLight: '--color-text-secondary-on-light',
  colorTextPrimaryOnDark: '--color-text-primary-on-dark',
  colorTextSecondaryOnDark: '--color-text-secondary-on-dark',
  colorTextMainNavHover: '--color-text-main-nav-hover',
  colorTextSecondaryNavHover: '--color-text-secondary-nav-hover',
  // Colors - Highlights
  colorHighlightPrimary: '--color-highlight-primary',
  colorHighlightPrimaryHover: '--color-highlight-primary-hover',
  colorHighlightSecondary: '--color-highlight-secondary',
  // Colors - Buttons
  colorButtonPrimary: '--color-button-primary',
  colorButtonPrimaryHover: '--color-button-primary-hover',
  colorButtonSecondary: '--color-button-secondary',
  colorButtonSecondaryHover: '--color-button-secondary-hover',
  // Colors - Form Inputs
  colorBackgroundInput: '--color-background-input',
  colorInputBorder: '--color-input-border',
  colorInputBorderFocused: '--color-input-border-focused',
  colorInputError: '--color-input-error',
  // Typography Sizes
  size3xs: '--size-3xs',
  size2xs: '--size-2xs',
  sizeXs: '--size-xs',
  sizeS: '--size-s',
  sizeM: '--size-m',
  sizeL: '--size-l',
  sizeXl: '--size-xl',
  size2xl: '--size-2xl',
  size3xl: '--size-3xl',
  size4xl: '--size-4xl',
  // Spacing
  spacing1: '--spacing-1',
  spacing2: '--spacing-2',
  spacing3: '--spacing-3',
  spacing4: '--spacing-4',
  spacing5: '--spacing-5',
  spacing6: '--spacing-6',
  spacing7: '--spacing-7',
  spacing8: '--spacing-8',
  spacing9: '--spacing-9',
  spacing10: '--spacing-10',
  spacing11: '--spacing-11',
  spacing12: '--spacing-12',
  spacing13: '--spacing-13',
  spacing14: '--spacing-14',
  spacing15: '--spacing-15',
  // Border Radius
  borderRadiusXs: '--border-radius-xs',
  borderRadiusS: '--border-radius-s',
  borderRadiusM: '--border-radius-m',
  borderRadiusL: '--border-radius-l',
  borderRadiusXl: '--border-radius-xl',
  borderRadiusRound: '--border-radius-round',
  // Shadows
  shadow1: '--shadow-1',
  // Fonts
  fontFamilyBase: '--font-family-base',
};

// Default theme values (fallback)
export const DEFAULT_THEME: ThemeTokens = {
  // Colors - Backgrounds
  colorBackgroundPrimaryLight: '#ffffff',
  colorBackgroundSecondaryLight: '#fcf5f6',
  colorBackgroundAlternative: '#f60d2e',
  colorBackgroundHeader: '#f60d2e',
  // Colors - Text
  colorTextPrimaryOnLight: '#1f1f1f',
  colorTextSecondaryOnLight: '#666666',
  colorTextPrimaryOnDark: '#ffffff',
  colorTextSecondaryOnDark: '#999999',
  colorTextMainNavHover: '#999999',
  colorTextSecondaryNavHover: '#cccccc',
  // Colors - Highlights
  colorHighlightPrimary: '#f60d2e',
  colorHighlightPrimaryHover: '#f60d2e',
  colorHighlightSecondary: '#fcf5f6',
  // Colors - Buttons
  colorButtonPrimary: '#f60d2e',
  colorButtonPrimaryHover: '#f60d2e',
  colorButtonSecondary: '#e6f0ee',
  colorButtonSecondaryHover: '#fbf5f6',
  // Colors - Form Inputs
  colorBackgroundInput: '#ffffff',
  colorInputBorder: '#cccccc',
  colorInputBorderFocused: '#1f1f1f',
  colorInputError: '#ff0000',
  // Typography Sizes
  size3xs: '1.2rem',
  size2xs: '1.4rem',
  sizeXs: '1.6rem',
  sizeS: '1.8rem',
  sizeM: '2.4rem',
  sizeL: '3.2rem',
  sizeXl: '4rem',
  size2xl: '4.8rem',
  size3xl: '5.6rem',
  size4xl: '6.4rem',
  // Spacing
  spacing1: '0.2rem',
  spacing2: '0.4rem',
  spacing3: '0.8rem',
  spacing4: '1.2rem',
  spacing5: '1.6rem',
  spacing6: '2.4rem',
  spacing7: '3.2rem',
  spacing8: '4rem',
  spacing9: '4.8rem',
  spacing10: '5.6rem',
  spacing11: '6.4rem',
  spacing12: '7.2rem',
  spacing13: '8rem',
  spacing14: '9.6rem',
  spacing15: '16rem',
  // Border Radius
  borderRadiusXs: '0.2rem',
  borderRadiusS: '0.4rem',
  borderRadiusM: '0.8rem',
  borderRadiusL: '1.2rem',
  borderRadiusXl: '1.6rem',
  borderRadiusRound: '25%',
  // Shadows
  shadow1: '0 8px 16px rgba(0, 0, 0, 0.16)',
  // Fonts
  fontFamilyBase: 'Flexo',
};

export function applyTheme(theme: ThemeTokens | null | undefined) {
  const root = document.documentElement;
  const effectiveTheme = { ...DEFAULT_THEME, ...theme };

  Object.entries(effectiveTheme).forEach(([key, value]) => {
    const cssVar = tokenToCssVar[key as keyof ThemeTokens];
    if (cssVar && value) {
      root.style.setProperty(cssVar, value);
    }
  });
}

export function useTheme(theme: ThemeTokens | null | undefined) {
  useEffect(() => {
    if (typeof window === 'undefined') return;
    applyTheme(theme);
  }, [theme]);
}
